import { ACTION_BASE_EVENTS, allOf, formResult, signalEquals, ACTION_TYPE_EVENTS, COMPLETING_NODE_TYPES, RUNTIME_EVENTS, TOOL_EVENTS, TRANSFER_EVENT_LABELS, TRANSFER_NODE_EVENTS, graphSchema, isEntryEdge, startConversationOpening, canonicalCondition, renameConditionNodes, validateGraph, } from '@super/shared-types';
/**
 * The canvas as the operator draws it, and the two translations between it and
 * the stored graph.
 *
 * The canvas holds one extra thing the graph does not: two pseudo-nodes. Start
 * is where the entry edge comes from and End is where an edge that ends the
 * call points. Everything else maps one to one, ids included: a node id and an
 * edge id the operator did not change survive a save, because the API diffs
 * rows by id.
 */
/**
 * Pseudo-node ids on the canvas. Neither is ever a node in the graph.
 *
 * The trigger pill is what the entry edge leaves from; the Start conversation
 * node is what it points at. The stored value stays `__start__`, because a
 * saved graph keys its layout positions by it.
 */
export const TRIGGER_NODE_ID = '__start__';
export const END_NODE_ID = '__end__';
const FLOW_START_PREFIX = '__flow__:';
/** The canvas node a cross-cutting flow starts from. */
export function flowStartNodeId(flowId) {
    return `${FLOW_START_PREFIX}${flowId}`;
}
export function isFlowStartNodeId(id) {
    return id.startsWith(FLOW_START_PREFIX);
}
/** The trigger or a cross-cutting flow: a pill with one arrow out, none in. */
export function isEntryPill(id) {
    return id === TRIGGER_NODE_ID || isFlowStartNodeId(id);
}
/** Canvas node types the author selects and edits in the side panel. */
export function isSelectableNodeType(type) {
    return type === 'step' || type === 'form' || type === 'flowStart';
}
/** The flow, and the edge it is stored as, that a start node belongs to. */
export function flowIdOf(nodeId) {
    return nodeId.slice(FLOW_START_PREFIX.length);
}
export function newId() {
    return crypto.randomUUID();
}
/**
 * A pre-greeting arrow: an arrow out of the Start conversation node with a
 * condition, checked when the call starts, before anyone speaks. The arrow out
 * of Start without one is "All other calls".
 */
export function isPreGreetingArrow(arrow, stepById) {
    return (stepById.get(arrow.source)?.type === 'start_conversation' &&
        arrow.condition !== undefined);
}
/** Start's arrows in the order they are checked: the pre-greeting arrows,
 *  then "All other calls". */
export function startCheckOrder(arrows, preGreeting) {
    return [
        ...arrows.filter(preGreeting),
        ...arrows.filter(arrow => !preGreeting(arrow)),
    ];
}
/** The pre-greeting arrow into this node, if there is one. */
export function preGreetingArrowTo(state, nodeId) {
    const stepById = new Map(state.steps.map(step => [step.id, step]));
    return state.arrows.find(arrow => isPreGreetingArrow(arrow, stepById) && arrow.target === nodeId);
}
/** How a node of `type` put on a pre-greeting arrow says the greeting of the
 *  node after it: as its own greeting, or as a say node's line. Other types
 *  say nothing first, so they take none. */
export function takenGreeting(type, greeting) {
    if (type === 'say')
        return { text: greeting };
    if (type === 'conversation' || type === 'form' || type === 'transfer')
        return { startGreeting: greeting };
    return undefined;
}
/** Where "All other calls" leads: the node a call goes to when no pre-greeting arrow holds. */
export function allOtherCallsTarget(state) {
    const start = state.steps.find(step => step.type === 'start_conversation');
    const arrow = state.arrows.find(a => a.source === start?.id && a.condition === undefined);
    if (!arrow || arrow.target === END_NODE_ID)
        return null;
    return arrow.target;
}
/** The node the call starts at: what the entry arrow points to. */
export function entryNodeId(state) {
    const entry = state.arrows.find(arrow => arrow.source === TRIGGER_NODE_ID);
    if (!entry || entry.target === END_NODE_ID)
        return null;
    return entry.target;
}
export const TOOL_OPTIONS = [
    {
        id: 'SWITCH_LANGUAGE',
        label: 'Language switching',
        description: 'Switch between English and Spanish during the call.',
    },
    {
        id: 'GET_PROPERTY_LISTINGS',
        label: 'Property listings',
        description: 'Look up and describe property listings.',
    },
    {
        id: 'TENANT_SHOWING_SCHEDULING',
        label: 'Tenant showing scheduling',
        description: 'Book a showing with a tenant. Raises "tour scheduled".',
    },
    {
        id: 'OWNER_MEETING_SCHEDULING',
        label: 'Owner meeting scheduling',
        description: 'Book a meeting with an owner. Raises "meeting booked".',
    },
];
const EVENT_COPY = {
    // Never offered: the entry edge waits for this one, and the editor writes
    // that edge itself rather than asking anyone to choose it.
    [RUNTIME_EVENTS.SESSION_STARTED]: {
        label: 'Call started',
        description: 'The far end is on the line.',
    },
    [RUNTIME_EVENTS.TEXT_DELIVERED]: {
        label: 'Line finished playing',
        description: 'The say node reached the end of its line.',
    },
    voicemail_detected: {
        label: 'Voicemail detected',
        description: 'Voicemail detection heard a machine on the line.',
    },
    meeting_booked: {
        label: 'Meeting booked',
        description: 'Owner meeting scheduling booked a meeting.',
    },
    tour_scheduled: {
        label: 'Tour scheduled',
        description: 'Tenant showing scheduling booked a tour.',
    },
    transfer_declined: {
        label: TRANSFER_EVENT_LABELS.transfer_declined,
        description: 'No one accepted, no mailbox answered, and at least one team member answered and did not accept.',
    },
    transfer_voicemail: {
        label: TRANSFER_EVENT_LABELS.transfer_voicemail,
        description: 'No one accepted and at least one number went to a mailbox. If a message is set, the agent left it.',
    },
    transfer_timeout: {
        label: TRANSFER_EVENT_LABELS.transfer_timeout,
        description: 'No one accepted, no mailbox, no live answer, and at least one number rang through unanswered.',
    },
    transfer_failed: {
        label: TRANSFER_EVENT_LABELS.transfer_failed,
        description: 'The phone tree is exhausted and nobody took the call. Also fires for declined, voicemail, or not answered when that outcome has no edge of its own.',
    },
    transfer_completed: {
        label: TRANSFER_EVENT_LABELS.transfer_completed,
        description: 'A team member took the call and the call with them ended. The call is over; route to an end node.',
    },
    transfer_connected: {
        label: TRANSFER_EVENT_LABELS.transfer_connected,
        description: 'The caller is with the team member. No edge routes on it.',
    },
    transfer_hangup: {
        label: TRANSFER_EVENT_LABELS.transfer_hangup,
        description: 'The caller left the merged call. No edge routes on it.',
    },
};
export function eventLabel(name) {
    return EVENT_COPY[name]?.label ?? name.replace(/_/g, ' ');
}
/** What an event means, in a sentence; empty when the editor has none. */
export function eventDescription(name) {
    return EVENT_COPY[name]?.description ?? '';
}
function option(name) {
    return { name, label: eventLabel(name), description: eventDescription(name) };
}
/**
 * The events a node can raise. A say node raises one when its line has played.
 * A conversation or form node raises what its tools raise; a form's arrows
 * also read its outputs through equation rows.
 */
export function eventsFrom(step) {
    if (!step)
        return [];
    if (step.type === 'say')
        return [option(RUNTIME_EVENTS.TEXT_DELIVERED)];
    // End conversation nodes have no outgoing events in v1; post-call routing
    // on `conversation_ended` arrives with the post-conversation phase (T4).
    if (step.type === 'end_conversation')
        return [];
    // Action nodes emit two layers: base events every action type emits, plus
    // per-type outcome events. Each action type lands its outcomes with its
    // own ticket; today the per-type dict is empty for every type.
    if (step.type === 'action') {
        const names = new Set([
            ...ACTION_BASE_EVENTS,
            ...(ACTION_TYPE_EVENTS[step.actionType] ?? []),
        ]);
        return [...names].map(option);
    }
    // Transfer nodes raise one of the outcome events, exactly one of which
    // fires per dispatch.
    if (step.type === 'transfer') {
        return TRANSFER_NODE_EVENTS.map(option);
    }
    // Start moves on as soon as the conversation opens, which the editor sets
    // itself, so it offers no event to pick.
    if (step.type === 'start_conversation')
        return [];
    const names = new Set();
    for (const { tool } of step.tools) {
        for (const event of TOOL_EVENTS[tool] ?? [])
            names.add(event);
    }
    return [...names].map(option);
}
function fail(message, issues = []) {
    return { success: false, message, issues };
}
export function compileCanvas(state, meta, layoutMetadata) {
    const entry = state.arrows.filter(arrow => arrow.source === TRIGGER_NODE_ID);
    if (entry.length === 0) {
        return fail('Connect the trigger to a node before saving');
    }
    const stepById = new Map(state.steps.map(step => [step.id, step]));
    for (const step of state.steps) {
        const label = step.title.trim() || step.id;
        if (!step.title.trim())
            return fail('Give every node a title');
        if (step.type === 'conversation' && !step.prompt.trim()) {
            return fail(`"${label}" needs a prompt`);
        }
        if (step.type === 'say' && !step.text.trim()) {
            return fail(`"${label}" needs the line it says`);
        }
        if (step.type === 'form' &&
            step.items.every(item => item.text.trim() === '')) {
            return fail(`"${label}" needs at least one question or prompt`);
        }
    }
    if (!state.steps.some(step => step.type === 'end_conversation')) {
        return fail('Add an End conversation node before saving');
    }
    for (const flow of state.flowStarts) {
        const out = state.arrows.filter(arrow => arrow.source === flowStartNodeId(flow.id));
        if (flow.events.length === 0) {
            return fail('Pick what starts each cross-cutting flow');
        }
        if (out.length !== 1) {
            return fail('Connect each cross-cutting flow to the node it starts at');
        }
    }
    for (const arrow of state.arrows) {
        if (arrow.source === TRIGGER_NODE_ID || isFlowStartNodeId(arrow.source)) {
            continue;
        }
        const from = stepById.get(arrow.source);
        // End conversation nodes own unconditional edges, same as the start node.
        // The post-conversation phase walks them independently of this arrow's event.
        if (from?.type === 'end_conversation')
            continue;
        const name = from?.title.trim() || arrow.source;
        if (from?.type === 'say') {
            const outgoing = state.arrows.filter(a => a.source === from.id);
            if (outgoing.length > 1) {
                return fail(`"${name}" says one line, so it has one way out. Remove the extra transitions.`);
            }
        }
        if (from?.type === 'start_conversation') {
            const otherwise = state.arrows.filter(a => a.source === from.id && a.condition === undefined);
            if (otherwise.length === 0) {
                return fail(`Leave one arrow out of "${name}" without a condition. The call takes it when no pre-greeting arrow holds.`);
            }
            if (otherwise.length > 1) {
                return fail(`Only one arrow out of "${name}" can be without a condition. Give the others a condition.`);
            }
            if (isPreGreetingArrow(arrow, stepById)) {
                const rows = arrow.condition?.rows ?? [];
                const to = arrow.target === END_NODE_ID
                    ? 'End'
                    : stepById.get(arrow.target)?.title.trim() || 'a node';
                if (rows.length === 0) {
                    return fail(`The pre-greeting arrow to "${to}" needs a condition`);
                }
                if (rows.some(row => row.kind !== 'value')) {
                    return fail(`The pre-greeting arrow to "${to}" can read only system and contact variables`);
                }
                continue;
            }
        }
        // An arrow the editor sets itself waits on its node's own event.
        if (implicitEvent(arrow, stepById))
            continue;
        // A transfer or action node moves on once it completes, so an arrow
        // out of one may have no condition.
        const rows = arrow.condition?.rows ?? [];
        if (rows.length === 0 &&
            !(from && COMPLETING_NODE_TYPES.includes(from.type))) {
            return fail(`The transition out of "${name}" needs a condition`);
        }
        // The contract accepts an event a node cannot raise; the call then waits
        // on it forever. Catch it here, where the operator drew it. An intent the
        // graph watches for is raised in every node, so it counts too.
        for (const row of rows) {
            if (row.kind !== 'event' || state.watchEvents.includes(row.event))
                continue;
            const raiser = row.nodeId ? stepById.get(row.nodeId) : from;
            if (!eventsFrom(raiser).some(choice => choice.name === row.event)) {
                return fail(`"${raiser?.title.trim() || name}" never raises "${eventLabel(row.event)}". Pick another condition, or switch on the tool that raises it.`);
            }
        }
    }
    const nodeLayoutMetadata = (id) => layoutMetadata?.nodes?.[id]
        ? {
            position: {
                x: layoutMetadata.nodes[id].x,
                y: layoutMetadata.nodes[id].y,
            },
        }
        : null;
    const startPos = layoutMetadata?.nodes?.[TRIGGER_NODE_ID];
    const endPos = layoutMetadata?.nodes?.[END_NODE_ID];
    const edgeLayoutMetadata = (arrow) => {
        const base = {};
        const label = layoutMetadata?.edges?.[arrow.id];
        if (label)
            base.labelPos = { x: label.x, y: label.y };
        // Entry edge carries the Start pill position; any edge to End carries
        // the End pill position. Those pills are not persisted as rows, so the
        // edge rows are the only place to attach their layout.
        if (arrow.source === TRIGGER_NODE_ID && startPos) {
            base.sourceNodePos = { x: startPos.x, y: startPos.y };
        }
        // A cross-cutting flow's start node is not a row either; its edge holds
        // where it sits, the way the entry edge holds Start.
        const flowPos = isFlowStartNodeId(arrow.source)
            ? layoutMetadata?.nodes?.[arrow.source]
            : undefined;
        if (flowPos) {
            base.sourceNodePos = { x: flowPos.x, y: flowPos.y };
        }
        if (arrow.target === END_NODE_ID && endPos) {
            base.targetNodePos = { x: endPos.x, y: endPos.y };
        }
        return Object.keys(base).length > 0 ? base : null;
    };
    // A greeting left empty is no greeting; a place before a flow that was
    // removed is the default place.
    const flowIds = new Set(state.flowStarts.map(flow => flow.id));
    const routing = (step) => ({
        ...(step.startGreeting?.trim()
            ? { startGreeting: step.startGreeting.trim() }
            : {}),
        ...(step.ownArrowsBefore && flowIds.has(step.ownArrowsBefore)
            ? { ownArrowsBefore: step.ownArrowsBefore }
            : {}),
    });
    const nodes = state.steps.map((step, position) => {
        const meta = nodeLayoutMetadata(step.id);
        const base = {
            id: step.id,
            title: step.title,
            position,
            ...(meta ? { layoutMetadata: meta } : {}),
        };
        if (step.type === 'say') {
            return { ...base, type: 'say', text: step.text };
        }
        if (step.type === 'form') {
            // Strip trailing/placeholder items that have no text. The author
            // leaves one behind after every Enter (new empty line below the
            // committed one); saving should not persist those.
            const nonEmptyItems = step.items.filter(item => item.text.trim() !== '');
            return {
                ...base,
                type: 'form',
                items: nonEmptyItems,
                tools: step.tools,
                knowledgeBaseIds: step.knowledgeBaseIds,
                excludedKnowledgeBaseIds: step.excludedKnowledgeBaseIds,
                ...routing(step),
            };
        }
        if (step.type === 'start_conversation') {
            return {
                ...base,
                type: 'start_conversation',
                channels: step.channels,
                destinationVariable: step.destinationVariable ?? null,
            };
        }
        if (step.type === 'end_conversation') {
            // Same canonicalization as form items and signals: empty authoring
            // state goes back to null on save.
            const signOff = step.signOff &&
                (step.signOff.promptText.trim() !== '' ||
                    step.signOff.exactText.trim() !== '')
                ? step.signOff
                : null;
            return { ...base, type: 'end_conversation', signOff };
        }
        if (step.type === 'action') {
            return {
                ...base,
                type: 'action',
                actionType: step.actionType,
                nodeActionGroupId: step.nodeActionGroupId,
            };
        }
        if (step.type === 'transfer') {
            return {
                ...base,
                type: 'transfer',
                workflowActionId: step.workflowActionId,
                ...routing(step),
            };
        }
        // A signal the author started and never wrote is the same empty block a
        // form leaves behind; save what was written and nothing else.
        const signals = step.signals.filter(signal => signal.text.trim() !== '');
        return {
            ...base,
            type: 'conversation',
            prompt: step.prompt,
            knowledgeBaseIds: step.knowledgeBaseIds,
            excludedKnowledgeBaseIds: step.excludedKnowledgeBaseIds,
            tools: step.tools,
            ...(signals.length > 0 ? { signals } : {}),
            ...(step.speaksOnEntry ? { speaksOnEntry: true } : {}),
            ...routing(step),
        };
    });
    // Two conditions are not a choice anyone makes, so the editor writes them.
    // The contract wants exactly one edge waiting for session_started; a say
    // node moves on once its line has played. Everything else carries what the
    // operator set.
    // Cross-cutting flows are written in the order of their list, since that
    // order is their priority; every other edge keeps the order it has.
    const flowById = new Map(state.flowStarts.map(flow => [flow.id, flow]));
    const flowRank = new Map(state.flowStarts.map((flow, i) => [flow.id, i]));
    const flowArrows = state.arrows
        .filter(arrow => isFlowStartNodeId(arrow.source))
        .sort((a, b) => (flowRank.get(flowIdOf(a.source)) ?? 0) -
        (flowRank.get(flowIdOf(b.source)) ?? 0));
    const ordered = [
        ...state.arrows.filter(arrow => arrow.source === TRIGGER_NODE_ID),
        ...flowArrows,
        ...state.arrows.filter(arrow => arrow.source !== TRIGGER_NODE_ID && !isFlowStartNodeId(arrow.source)),
    ];
    const edges = ordered.map((arrow, position) => {
        const meta = edgeLayoutMetadata(arrow);
        const flow = isFlowStartNodeId(arrow.source)
            ? flowById.get(flowIdOf(arrow.source))
            : undefined;
        return {
            id: flow ? flow.id : arrow.id,
            fromNodeId: arrow.source === TRIGGER_NODE_ID || flow ? null : arrow.source,
            toNodeId: arrow.target === END_NODE_ID ? null : arrow.target,
            condition: flow ? flowCondition(flow) : storedCondition(arrow, stepById),
            position,
            ...(arrow.label?.trim() ? { label: arrow.label.trim() } : {}),
            ...(meta ? { layoutMetadata: meta } : {}),
        };
    });
    const parsed = graphSchema.safeParse({
        name: meta.name,
        persona: state.persona,
        ...(state.voice ? { voiceSettings: { voice: state.voice } } : {}),
        // Omitted when unset, so a graph without a trigger round-trips to
        // exactly what it was written as.
        ...(state.trigger ? { trigger: state.trigger } : {}),
        active: meta.active ?? true,
        knowledgeBaseIds: state.knowledgeBaseIds,
        watchEvents: state.watchEvents,
        nodes,
        edges,
    });
    if (!parsed.success) {
        // Map each Zod issue to a GraphIssue so the author lands on the invalid
        // node or edge on save. Path shapes we see here: ['edges', N, ...] and
        // ['nodes', N, ...].
        const zodIssues = parsed.error.issues.map(issue => {
            const [root, index] = issue.path;
            let ref;
            if ((root === 'edges' || root === 'nodes') && typeof index === 'number') {
                const target = root === 'edges' ? edges[index] : nodes[index];
                ref = target?.id;
            }
            const message = issue.path.length
                ? `${issue.path.join('.')}: ${issue.message}`
                : issue.message;
            return { code: 'invalid_shape', message, ref };
        });
        const summary = zodIssues
            .slice(0, 3)
            .map(i => i.message)
            .join(' · ');
        return fail(summary, zodIssues);
    }
    // The contract's own rules, which are about references between rows rather
    // than about what the operator typed. Its messages name rows by id, so swap
    // each id for the title on the canvas before anyone reads it.
    const issues = validateGraph(parsed.data);
    if (issues.length > 0) {
        const named = issues.map(issue => ({
            ...issue,
            message: nameRows(issue.message, stepById),
        }));
        return fail(named.map(issue => issue.message).join(' · '), named);
    }
    return { success: true, data: parsed.data };
}
/** Swap any node id in a contract message for the title the operator sees. */
function nameRows(message, stepById) {
    let named = message;
    for (const [id, step] of stepById) {
        if (!named.includes(id))
            continue;
        named = named.split(id).join(step.title.trim() || 'an untitled node');
    }
    return named;
}
/**
 * Whether arrows out of this source carry their condition without being
 * asked: Start and every node `implicitEvent` names.
 */
export function isImplicitSource(source, stepById) {
    return implicitEvent({ id: '', source, target: '' }, stepById) !== null;
}
/**
 * The condition a transition carries without being asked, or null when the
 * operator chooses it. Out of Start the call has begun; out of a say node the
 * line has finished. Neither is a decision, so neither gets a control.
 */
export function implicitEvent(arrow, stepById) {
    if (arrow.source === TRIGGER_NODE_ID)
        return RUNTIME_EVENTS.SESSION_STARTED;
    const type = stepById.get(arrow.source)?.type;
    // A pre-greeting arrow carries its own condition.
    if (type === 'start_conversation') {
        return arrow.condition ? null : RUNTIME_EVENTS.CONVERSATION_STARTED;
    }
    if (type === 'say')
        return RUNTIME_EVENTS.TEXT_DELIVERED;
    if (type === 'end_conversation')
        return RUNTIME_EVENTS.CONVERSATION_ENDED;
    return null;
}
/**
 * Open a stored graph on the canvas. One shape is refused rather than drawn:
 * a cross-cutting edge that does not wait on all of a set of events. The flow
 * panel draws only that, so opening it would drop the rest on the next save.
 */
export function decompileGraph(graph) {
    const steps = [...graph.nodes]
        .sort((a, b) => a.position - b.position)
        .map(node => {
        if (node.type === 'say') {
            return {
                id: node.id,
                type: 'say',
                title: node.title,
                text: node.text,
            };
        }
        if (node.type === 'form') {
            return {
                id: node.id,
                type: 'form',
                title: node.title,
                items: node.items.map(item => ({ ...item })),
                tools: node.tools.map(tool => ({ ...tool })),
                knowledgeBaseIds: [...node.knowledgeBaseIds],
                excludedKnowledgeBaseIds: [...node.excludedKnowledgeBaseIds],
                ...routingOf(node),
            };
        }
        if (node.type === 'start_conversation') {
            return {
                id: node.id,
                type: 'start_conversation',
                title: node.title,
                channels: [...node.channels],
                destinationVariable: node.destinationVariable ?? null,
            };
        }
        if (node.type === 'end_conversation') {
            return {
                id: node.id,
                type: 'end_conversation',
                title: node.title,
                signOff: node.signOff ? { ...node.signOff } : null,
            };
        }
        if (node.type === 'action') {
            return {
                id: node.id,
                type: 'action',
                title: node.title,
                actionType: node.actionType,
                nodeActionGroupId: node.nodeActionGroupId,
            };
        }
        if (node.type === 'transfer') {
            return {
                id: node.id,
                type: 'transfer',
                title: node.title,
                workflowActionId: node.workflowActionId,
                ...routingOf(node),
            };
        }
        if (node.type === 'conversation') {
            return {
                id: node.id,
                type: 'conversation',
                title: node.title,
                prompt: node.prompt,
                knowledgeBaseIds: [...node.knowledgeBaseIds],
                excludedKnowledgeBaseIds: [...node.excludedKnowledgeBaseIds],
                tools: node.tools.map(tool => ({ ...tool })),
                signals: (node.signals ?? []).map(signal => ({ ...signal })),
                ...(node.speaksOnEntry ? { speaksOnEntry: true } : {}),
                ...routingOf(node),
            };
        }
        throw new Error(`decompileGraph met an unknown node variant: ${JSON.stringify(node)}`);
    });
    const stepById = new Map(steps.map(step => [step.id, step]));
    const arrows = [];
    const flowStarts = [];
    for (const edge of [...graph.edges].sort((a, b) => a.position - b.position)) {
        if (edge.fromNodeId == null && !isEntryEdge(edge)) {
            const events = flowEvents(edge.condition);
            if (!events) {
                return {
                    success: false,
                    message: 'This graph has an edge from anywhere that does not wait on all of a set of events, or names the node of one. The editor cannot draw it yet, so it will not open it rather than drop it.',
                };
            }
            flowStarts.push({ id: edge.id, events });
            arrows.push({
                id: edge.id,
                source: flowStartNodeId(edge.id),
                target: edge.toNodeId ?? END_NODE_ID,
            });
            continue;
        }
        const arrow = {
            id: edge.id,
            source: edge.fromNodeId ?? TRIGGER_NODE_ID,
            target: edge.toNodeId ?? END_NODE_ID,
            ...(edge.label ? { label: edge.label } : {}),
        };
        // A pre-greeting arrow carries its rows; "All other calls" carries none.
        if (edge.condition.type === 'pre_greeting') {
            const { mode, rows } = edge.condition;
            arrows.push({ ...arrow, condition: { type: 'equation', mode, rows } });
            continue;
        }
        arrows.push(implicitEvent(arrow, stepById)
            ? arrow
            : { ...arrow, condition: asEquation(edge.condition) });
    }
    const nodePositions = {};
    for (const node of graph.nodes) {
        const pos = node.layoutMetadata?.position;
        if (pos)
            nodePositions[node.id] = { x: pos.x, y: pos.y };
    }
    const edgeLabelPositions = {};
    // The Start pill is carried on the entry edge, the End pill on any edge to
    // End. First one found wins — all edges-to-End store the same position.
    for (const edge of graph.edges) {
        const pos = edge.layoutMetadata?.labelPos;
        edgeLabelPositions[edge.id] = pos ? { x: pos.x, y: pos.y } : null;
        const srcPos = edge.layoutMetadata?.sourceNodePos;
        if (srcPos && edge.fromNodeId == null) {
            const flow = flowStarts.find(f => f.id === edge.id);
            nodePositions[flow ? flowStartNodeId(flow.id) : TRIGGER_NODE_ID] = {
                x: srcPos.x,
                y: srcPos.y,
            };
        }
        const tgtPos = edge.layoutMetadata?.targetNodePos;
        if (tgtPos && edge.toNodeId == null && !nodePositions[END_NODE_ID]) {
            nodePositions[END_NODE_ID] = { x: tgtPos.x, y: tgtPos.y };
        }
    }
    return {
        success: true,
        data: {
            persona: graph.persona,
            ...(graph.voiceSettings?.voice
                ? { voice: graph.voiceSettings.voice }
                : {}),
            trigger: graph.trigger ?? null,
            knowledgeBaseIds: [...graph.knowledgeBaseIds],
            watchEvents: [...graph.watchEvents],
            steps,
            arrows,
            flowStarts,
        },
        layoutMetadata: {
            nodes: nodePositions,
            edges: edgeLabelPositions,
        },
    };
}
/** A node's start greeting and place among the flows, when it has them. */
function routingOf(node) {
    return {
        ...(node.startGreeting ? { startGreeting: node.startGreeting } : {}),
        ...(node.ownArrowsBefore ? { ownArrowsBefore: node.ownArrowsBefore } : {}),
    };
}
/**
 * Open a template on the canvas, with a fresh set of ids. Every consumer that
 * turns a template into something the operator edits goes through here, so the
 * ids are never the ones written in this file.
 *
 * Throws when the template cannot be drawn, which is a mistake in this package
 * rather than something a consumer can handle. A stored graph goes through
 * `decompileGraph` instead, which reports rather than throws.
 */
export function templateCanvas(graph) {
    const result = decompileGraph(withFreshIds(graph));
    if (!result.success) {
        throw new Error(`template "${graph.name}" cannot be drawn: ${result.message}`);
    }
    return result.data;
}
/* ---------- templates ---------- */
/**
 * Give a graph a fresh set of node and edge ids, keeping every reference
 * between them. The ids the editor sends become the row ids, so a template
 * goes through here before it is saved or drawn. Two graphs made from one
 * template would otherwise ask for the same rows.
 */
export function withFreshIds(graph) {
    const fresh = new Map(graph.nodes.map(node => [node.id, newId()]));
    const freshEdge = new Map(graph.edges.map(edge => [edge.id, newId()]));
    const next = (nodeId) => nodeId == null ? null : (fresh.get(nodeId) ?? nodeId);
    return {
        ...graph,
        nodes: graph.nodes.map(node => ({
            ...node,
            id: fresh.get(node.id),
            ...('ownArrowsBefore' in node && node.ownArrowsBefore
                ? {
                    ownArrowsBefore: freshEdge.get(node.ownArrowsBefore) ?? node.ownArrowsBefore,
                }
                : {}),
        })),
        edges: graph.edges.map(edge => ({
            ...edge,
            id: freshEdge.get(edge.id),
            fromNodeId: next(edge.fromNodeId),
            toNodeId: next(edge.toNodeId),
            condition: renameConditionNodes(edge.condition, id => next(id)),
        })),
    };
}
// Ids in the templates below are placeholders: readable, well formed, and
// replaced by withFreshIds before a template reaches the canvas or the API.
const N = (n) => `a${String(n).repeat(7)}-aaaa-4aaa-8aaa-${String(n).repeat(12)}`;
const E = (n) => `b${String(n).repeat(7)}-aaaa-4aaa-8aaa-${String(n).repeat(12)}`;
/**
 * How a template opens: the entry edge into a Start node, and the edge out of
 * it. `N(9)` and `E(9)` are kept for those, so the ids a template already uses
 * do not move.
 */
function opening(toNodeId) {
    return startConversationOpening({
        startNodeId: N(9),
        entryEdgeId: E(1),
        exitEdgeId: E(9),
        toNodeId,
    });
}
/** The opening's two edges, for a template's `edges` array. */
function entryEdge(toNodeId, _edgeId) {
    return opening(toNodeId).edges;
}
export const BLANK_TEMPLATE = {
    name: 'New agent',
    persona: 'You are a friendly voice agent. One or two short spoken sentences per ' +
        'turn, one question per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [],
    nodes: [
        opening(N(1)).node,
        {
            id: N(1),
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hi, thanks for calling. How can I help?',
        },
        {
            id: N(2),
            type: 'conversation',
            title: 'New step',
            position: 2,
            prompt: 'Add a prompt to describe what happens in this step.',
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            tools: [],
        },
        {
            id: N(3),
            type: 'end_conversation',
            title: 'End conversation',
            position: 3,
            signOff: null,
        },
    ],
    edges: [
        ...entryEdge(N(1), E(1)),
        {
            id: E(2),
            fromNodeId: N(1),
            toNodeId: N(2),
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 2,
        },
    ],
};
export const STARTER_GRAPH = {
    name: 'Tour scheduler',
    persona: 'You are Sam, a friendly leasing assistant for a property management ' +
        'company. Keep replies short and conversational, one or two sentences ' +
        'at a time.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [],
    nodes: [
        opening(N(1)).node,
        {
            id: N(1),
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hi, this is Sam from the leasing office. Thanks for reaching out about the property. Do you have a quick minute?',
        },
        {
            id: N(2),
            type: 'conversation',
            title: 'Qualify and book the tour',
            position: 2,
            prompt: 'Ask what they are looking for: move-in date, bedrooms, and budget. ' +
                'One question per turn. Once you know enough, offer tour times and ' +
                'book the one they pick.',
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            // A tour is a tenant's, and this stands in until the capability for one
            // is built. That capability does more than schedule, so it arrives under
            // a different name; when it does, this template wants it instead.
            tools: [{ tool: 'OWNER_MEETING_SCHEDULING' }],
            // A lead who is not interested leaves for the feedback ask.
            signals: [
                {
                    id: 'not_interested',
                    text: 'The caller said they are no longer interested in the property',
                },
            ],
        },
        {
            id: N(3),
            type: 'end_conversation',
            title: 'End conversation',
            position: 3,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: 'You are all set. We will see you then. Have a great day.',
            },
        },
        {
            id: N(5),
            type: 'form',
            title: 'Ask for feedback',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            position: 4,
            items: [
                {
                    kind: 'question',
                    id: 'not_interested_reason',
                    text: 'No problem at all. Would you mind sharing what did not work for you, so I can pass it on to the team?',
                    answerType: { kind: 'free_text' },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
            ],
        },
        {
            id: N(6),
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: 'Thanks for letting us know. Have a great day.',
            },
        },
    ],
    edges: [
        ...entryEdge(N(1), E(1)),
        {
            id: E(2),
            fromNodeId: N(1),
            toNodeId: N(2),
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 2,
        },
        {
            id: E(3),
            fromNodeId: N(2),
            toNodeId: N(3),
            condition: { type: 'event', event: 'meeting_booked' },
            position: 3,
        },
        {
            id: E(7),
            fromNodeId: N(2),
            toNodeId: N(5),
            condition: allOf(signalEquals(N(2), 'not_interested', true)),
            position: 4,
        },
        {
            id: E(8),
            fromNodeId: N(5),
            toNodeId: N(6),
            condition: allOf(formResult(N(5), 'completed')),
            position: 5,
        },
    ],
};
export const MAINTENANCE_TEMPLATE = {
    name: 'Maintenance intake',
    persona: 'You are Jordan, a maintenance coordinator for a property management ' +
        'company. Be calm and efficient. One or two short spoken sentences per ' +
        'turn, one question per turn. Confirm what you heard back to the caller.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [],
    nodes: [
        opening(N(1)).node,
        {
            id: N(1),
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hi, this is Jordan at maintenance. What is happening at your unit?',
        },
        {
            id: N(2),
            type: 'conversation',
            title: 'Capture the issue',
            position: 2,
            prompt: 'Ask the caller to describe the issue in one or two sentences. Then ' +
                'ask whether it is urgent, meaning safety, water, no heat, or no ' +
                'power, or something that can wait. Confirm what you heard. If it is ' +
                'urgent, tell them the on-call tech calls back within fifteen ' +
                'minutes. If it can wait, offer a visit window in the next two ' +
                'business days.',
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            tools: [],
        },
        {
            id: N(3),
            type: 'end_conversation',
            title: 'End conversation',
            position: 3,
            signOff: null,
        },
    ],
    edges: [
        ...entryEdge(N(1), E(1)),
        {
            id: E(2),
            fromNodeId: N(1),
            toNodeId: N(2),
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 2,
        },
    ],
};
export const EMERGENCY_TEMPLATE = {
    name: 'Emergency triage',
    persona: 'You are Sam, the after-hours line for a property management company. ' +
        'Be calm. One or two short spoken sentences per turn. Screen quickly, ' +
        'and treat safety concerns as real until proved otherwise.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [],
    nodes: [
        opening(N(1)).node,
        {
            id: N(1),
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'You have reached after-hours support. What is happening?',
        },
        {
            id: N(2),
            type: 'conversation',
            title: 'Screen and route',
            position: 2,
            prompt: 'Ask the caller to describe the situation in one or two sentences. ' +
                'Listen for fire, flood, medical, break-in, no heat in winter, no ' +
                'water, or a gas smell. Treat any of those as an emergency: tell ' +
                'them you are paging the on-call staff and that someone calls back ' +
                'within fifteen minutes, and confirm their callback number. If it is ' +
                'cosmetic or a convenience, give them the business-hours number and ' +
                'ask them to call back.',
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            tools: [],
        },
        {
            id: N(3),
            type: 'end_conversation',
            title: 'End conversation',
            position: 3,
            signOff: null,
        },
    ],
    edges: [
        ...entryEdge(N(1), E(1)),
        {
            id: E(2),
            fromNodeId: N(1),
            toNodeId: N(2),
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 2,
        },
    ],
};
/**
 * What an arrow is stored with. The entry edge and a say node's exit wait on
 * their own event; an equation is stored in its canonical form.
 */
function storedCondition(arrow, stepById) {
    const implicit = implicitEvent(arrow, stepById);
    if (implicit)
        return { type: 'event', event: implicit };
    if (isPreGreetingArrow(arrow, stepById) && arrow.condition) {
        const rows = arrow.condition.rows.filter(row => row.kind === 'value');
        return { type: 'pre_greeting', mode: arrow.condition.mode, rows };
    }
    return canonicalCondition(arrow.condition ?? { type: 'equation', mode: 'all', rows: [] });
}
/**
 * A stored condition as the rows the editor draws. A condition kind the
 * contract no longer has keeps its kind as the event name, so the arrow shows
 * it and its row asks for a new condition.
 */
function asEquation(condition) {
    if (condition.type === 'equation')
        return condition;
    const stored = condition;
    return {
        type: 'equation',
        mode: 'all',
        rows: [{ kind: 'event', event: stored.event ?? stored.type }],
    };
}
/** The condition a cross-cutting flow is stored with. */
function flowCondition(flow) {
    if (flow.events.length === 1) {
        return { type: 'event', event: flow.events[0] };
    }
    return {
        type: 'equation',
        mode: 'all',
        rows: flow.events.map(event => ({ kind: 'event', event })),
    };
}
/**
 * The events an edge from anywhere starts a flow on, or null if its condition
 * is not "all of these events" with no node named: the editor draws nothing
 * else on a flow yet.
 */
function flowEvents(condition) {
    if (condition.type === 'event')
        return [condition.event];
    if (condition.type !== 'equation' || condition.mode !== 'all')
        return null;
    const events = [];
    for (const row of condition.rows) {
        if (row.kind !== 'event' || row.nodeId !== undefined)
            return null;
        events.push(row.event);
    }
    return events;
}
//# sourceMappingURL=schema.js.map